import type {DalValue, CreateExtArgs} from '@wix/document-manager-core'
import type {
    CompRef,
    Pointer,
    DependenciesManager,
    NamespacesDataMap,
    RefInfo,
    ComponentFeature,
    DependenciesBuilder
} from '@wix/document-services-types'
import _ from 'lodash'
import type {DataModelExtensionAPI} from '../../../../dataModel/dataModel'
import {
    DetailedValue,
    forEachReferredDataItem,
    getDetailedValueByPath,
    type ExtendedRefInfo,
    type ReferredDataItemsIterateeContext
} from '@wix/import-export-utils'
import {stripHashIfExists} from '../../../../../utils/refArrayUtils'
import type {RelationshipsAPI} from '../../../../relationships'
import type {SchemaExtensionAPI} from '../../../../..'
import {REFERABLE_BY_EXTERNAL_NODES, SPECIAL_DATA_NODE_IDS, type ExternalsHandler} from '../../constants'
import {getExternalsHandler} from '../externals'

interface DataItemsUpdate {
    pointer: Pointer
    origDataItem?: DalValue
    dataItem?: DalValue
}

type DataItemsUpdates = DataItemsUpdate[]

const sanitizeDataItem = (dataItem: DalValue, namespace: string) => {
    const {id, type} = dataItem
    if (!REFERABLE_BY_EXTERNAL_NODES[namespace]?.[type] && !SPECIAL_DATA_NODE_IDS[namespace]?.has(id)) {
        delete dataItem.id
    }
}

const collectNonOwnedReferences = (
    {pointers}: CreateExtArgs,
    item: DalValue,
    namespace: string,
    refFieldsInfo: ExtendedRefInfo[],
    dependenciesBuilder: DependenciesBuilder
) => {
    for (const {isRefOwner, path, constantValues, referencedMap} of refFieldsInfo) {
        if (isRefOwner) {
            continue
        }

        const valueByPath: DetailedValue[] = getDetailedValueByPath(item, path)
        const handleExternals: ExternalsHandler = getExternalsHandler(namespace, item.type, path)

        for (const detailedValue of valueByPath) {
            if (!constantValues?.has(detailedValue.value)) {
                const refId: string = stripHashIfExists(detailedValue.value)
                const refPointer: Pointer = pointers.getPointer(refId, referencedMap)

                handleExternals.pack(detailedValue, dependenciesBuilder, refPointer, item)
            }
        }
    }
}

export const resolveDataItem = (
    extensionContext: CreateExtArgs,
    itemPointer: Pointer,
    pagePointer: CompRef,
    dependenciesBuilder?: DependenciesBuilder
): DalValue => {
    const {extensionAPI, coreConfig} = extensionContext
    const {schemaService} = coreConfig
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const dataItem = dataModel.getItem(itemPointer.id, itemPointer.type, pagePointer.id, undefined, undefined, true)

    forEachReferredDataItem(
        schemaService,
        dataItem,
        itemPointer.type,
        ({item, namespace, refFieldsInfo}: ReferredDataItemsIterateeContext) => {
            dependenciesBuilder?.references.addInternalRef(item.id, namespace)
            sanitizeDataItem(item, namespace)
            if (dependenciesBuilder) {
                collectNonOwnedReferences(extensionContext, item, namespace, refFieldsInfo, dependenciesBuilder)
            }
        }
    )

    return dataItem
}

export const getResolvedDataItemsByNamespaceMap = (
    extensionContext: CreateExtArgs,
    dataPointers: Pointer[],
    pagePointer: CompRef,
    dependenciesBuilder?: DependenciesBuilder
): NamespacesDataMap | undefined => {
    if (dataPointers.length === 0) {
        return
    }

    const dataItemsMap: NamespacesDataMap = {}
    for (const pointer of dataPointers) {
        const dataItem = resolveDataItem(extensionContext, pointer, pagePointer, dependenciesBuilder)
        dataItemsMap[pointer.type] ??= {}
        dataItemsMap[pointer.type][pointer.id] = dataItem
    }

    return dataItemsMap
}

const shouldRemoveOriginalItem = (item: DalValue, originalItem?: DalValue) =>
    originalItem && originalItem.id !== item.id

const removeReference = (
    {dal, pointers}: CreateExtArgs,
    referringPointer: Pointer,
    path: string[],
    {isList}: RefInfo
) => {
    if (isList) {
        const itemIndex = _.toNumber(path.pop())
        const refsListPointer = pointers.getInnerPointer(referringPointer, path)
        const refsList = [...dal.get(refsListPointer)]
        refsList.splice(itemIndex, 1)
        dal.set(refsListPointer, refsList)
    } else {
        const referencePointer = pointers.getInnerPointer(referringPointer, path)
        dal.remove(referencePointer)
    }
}

const removeReferencesToItem = (extensionContext: CreateExtArgs, itemPointer: Pointer, namespace: string) => {
    const {dal, extensionAPI} = extensionContext
    const {relationships, schemaAPI} = extensionAPI as RelationshipsAPI & SchemaExtensionAPI
    const referringPointers = relationships.getReferencesToPointer(itemPointer)
    for (const referringPointer of referringPointers) {
        const referringItem = dal.get(referringPointer)
        const refFieldsInfo = schemaAPI.extractReferenceFieldsInfo(referringPointer.type, referringItem.type)
        for (const refFieldInfo of refFieldsInfo) {
            if (refFieldInfo.referencedMap !== namespace) {
                continue
            }

            const referredPointers = getDetailedValueByPath(referringItem, refFieldInfo.path)
            for (const {value, path} of referredPointers) {
                if (stripHashIfExists(value) === itemPointer.id) {
                    removeReference(extensionContext, referringPointer, path, refFieldInfo)
                }
            }
        }
    }
}

const removeOriginalItem = (extensionContext: CreateExtArgs, originalItem: DalValue, namespace: string) => {
    const {dal, pointers} = extensionContext
    const originalItemPointer = pointers.getPointer(originalItem.id, namespace)
    removeReferencesToItem(extensionContext, originalItemPointer, namespace)
    dal.remove(originalItemPointer)
}

const replaceSingleItemReferences =
    (extensionContext: CreateExtArgs, dependenciesManager: DependenciesManager) =>
    ({item, namespace, refFieldsInfo, originalItem}: ReferredDataItemsIterateeContext) => {
        item.id = dependenciesManager.references.getNewItemId(item.id, namespace, originalItem?.id)

        if (shouldRemoveOriginalItem(item, originalItem)) {
            removeOriginalItem(extensionContext, originalItem, namespace)
        }

        for (const refFieldInfo of refFieldsInfo) {
            const {isRefOwner, path} = refFieldInfo

            if (isRefOwner) {
                continue
            }

            const valueByPath: DetailedValue[] = getDetailedValueByPath(item, path)
            const handleExternals: ExternalsHandler = getExternalsHandler(namespace, item.type, path)

            for (const detailedValue of valueByPath) {
                handleExternals.unpack(detailedValue, dependenciesManager, refFieldInfo, item)
            }
        }
    }

const replaceDataItemReferences = (
    extensionContext: CreateExtArgs,
    dataItem: DalValue,
    origDataItem: DalValue,
    namespace: string,
    dependenciesManager: DependenciesManager
) => {
    const {coreConfig} = extensionContext
    const {schemaService} = coreConfig

    forEachReferredDataItem(
        schemaService,
        dataItem,
        namespace,
        replaceSingleItemReferences(extensionContext, dependenciesManager),
        {
            originalItem: origDataItem
        }
    )
}

export const updateComponentDataItem = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    dataItem: DalValue,
    {query, namespace}: ComponentFeature,
    dependenciesManager: DependenciesManager
) => {
    const {extensionAPI} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const origDataItem = dataModel.components.getItemByQuery(componentPointer, query, {raw: true})

    if (dataItem) {
        replaceDataItemReferences(extensionContext, dataItem, origDataItem, namespace, dependenciesManager)
        dataModel.components.addItemByQuery(componentPointer, query, dataItem, {
            skipOriginalNodeMerge: true,
            raw: true
        })
    } else if (origDataItem) {
        dataModel.components.removeItemByQuery(componentPointer, query)
    }
}

export const updateDataItem = (
    extensionContext: CreateExtArgs,
    dataItem: DalValue,
    origDataItem: DalValue,
    namespace: string,
    pagePointer: CompRef,
    dependenciesManager: DependenciesManager
) => {
    const {pointers, extensionAPI} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    if (dataItem) {
        replaceDataItemReferences(extensionContext, dataItem, origDataItem, namespace, dependenciesManager)
        dataModel.addItemWithRefReuse(dataItem, namespace, pagePointer.id, undefined, undefined, {raw: true})
    } else if (origDataItem) {
        dataModel.removeItemRecursivelyIncludingPermanentNodes(pointers.getPointer(origDataItem.id, namespace))
    }
}

export const updateDataItemsByNamespacesDataMap = (
    extensionContext: CreateExtArgs,
    origPointers: Pointer[],
    namespacesDataMap: NamespacesDataMap | undefined,
    pagePointer: CompRef,
    dependenciesManager: DependenciesManager
) => {
    const {pointers, extensionAPI} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const updates: DataItemsUpdates = origPointers.map((pointer: Pointer) => {
        const {id, type} = pointer
        const origDataItem = dataModel.getItem(id, type, pagePointer.id, undefined, undefined, true)
        const dataItem = namespacesDataMap?.[type]?.[id]
        delete namespacesDataMap?.[type]?.[id]

        return {
            pointer,
            origDataItem,
            dataItem
        }
    })

    _.forOwn(namespacesDataMap, (items: Record<string, DalValue>, namespace: string) => {
        _.forOwn(items, (dataItem: DalValue, id: string) => {
            updates.push({
                pointer: pointers.getPointer(id, namespace),
                dataItem
            })
        })
    })

    for (const {pointer, dataItem, origDataItem} of updates) {
        if (dataItem) {
            dataItem.id ??= pointer.id
            dependenciesManager.references.addReference(dataItem.id, pointer.type)
        }

        updateDataItem(extensionContext, dataItem, origDataItem, pointer.type, pagePointer, dependenciesManager)
    }
}
